import './Networking.css'
import NetworkingOpt from "../../components/seo/NetworkingOpt";

export default function NetworkAddressing() {
    return (
        <>
            <NetworkingOpt
                title="Network Addressing"
                description="Review IPv4 and IPv6 addressing, address classes, subnet masks, EUI-64, NDP, SLAAC, and duplicate address detection."
                keywords="network addressing, ipv4, ipv6, subnet mask, ndp, slaac, eui-64"
            />

            <article className='content'>
                <h1 style={{display:'flex', justifyContent:'center' }}>Network Addressing</h1>

                <h2>Introduction</h2>

                <p>Network addressing defines how devices identify and communicate across networks.</p>
                <p>It includes logical addressing, classification, and routing mechanisms for both IPv4 and IPv6.</p>
               
                <h2>Life of a Packet</h2>
               
                <p>A packet travels through multiple layers — from application to physical — encapsulated with headers at each layer.</p>
                <p>Understanding this journey helps diagnose connectivity and performance issues.</p>
                
                <h2>IPv4 Addressing</h2>
                <p>Logical Addressing</p>
                <p>IP addressing is logical, not physical. It identifies devices independently of hardware.</p>
                <h3>Address Classes</h3>
                <table>
                    <tr><td>Class</td><td>Range</td><td>Default Mask</td><td>Usage</td></tr>
                    <tr><td>A</td><td>1.0.0.0 – 126.255.255.255</td><td>255.0.0.0</td><td>Large networks</td></tr>
                    <tr><td>B</td><td>128.0.0.0 – 191.255.255.255</td><td>255.255.0.0</td><td>Medium networks</td></tr>
                    <tr><td>C</td><td>192.0.0.0 – 223.255.255.255</td><td>255.255.255.0</td><td>Small networks</td></tr>
                    <tr><td>D</td><td>224.0.0.0 – 239.255.255.255</td><td>N/A</td><td>Multicast</td></tr>
                    <tr><td>E</td><td>240.0.0.0 – 255.255.255.255</td><td>N/A</td><td>Experimental</td></tr>
                </table>
                <h3>Loopback Addresses</h3>
                <p>Range: 127.0.0.0 – 127.255.255.255</p>
                <p>Used to test the network stack locally.</p>

                <h3>Network Mask</h3>

                <p>Host portion all 0’s → Network Address (not assignable to hosts).</p>
                <p>Used to separate network and host portions.</p>

                <h3>Duplex Modes</h3>

                <p>Half Duplex: Cannot send and receive simultaneously.</p>
                <p>Full Duplex: Can send and receive at the same time.</p>

                <h3>Private IPv4 Addresses (RFC 1918)</h3>
                
                
                <p>Used for internal networks; not routable on the internet.</p>
                <p><em>Short-term solutions for IPv4 exhaustion:</em></p>
                <p>CIDR (Classless Inter-Domain Routing)</p>

                <p>NAT (Network Address Translation)</p>
                <p><em>Long-term solution: </em></p>
                <p>Transition to IPv6.</p>
                
                <h2> IPv6 Addressing</h2>
                <p>Modified EUI-64</p>
                <p>Converts a 48-bit MAC address into a 64-bit interface identifier:</p>
                <p>Split MAC in half.</p>
                <p>Insert FFFE in the middle.</p>
                <p>Invert the 7th bit (U/L bit).</p>
                <p>Command Example:</p>
                <pre>
                    <code>R1(config)# int g0/0</code><br />
                    <code>R1(config-if)# ipv6 address 2001:db8::/64 eui-64</code><br />
                    <code>R1(config-if)# no shutdown</code>
                </pre>
                
                <h3>MAC Address Types</h3>
                <table>
                    <tr><td>Type</td><td>Description</td><td>U/L Bit</td></tr>
                    <tr><td>UAA</td><td>Manufacturer-assigned, globally unique</td><td>0</td></tr>
                    <tr><td>LAA</td><td>Locally assigned by admin</td><td>1</td></tr>
                </table>
                
                <p>In IPv6 EUI-64, the U/L bit is reversed.</p>

                <h3>IPv6 Routing</h3>

                <h4>Static Routes</h4>
                <p>Syntax:</p>
                <pre>ipv6 route destination/prefix-length exit-interface [next-hop] [ad]</pre>

                <p>Types:</p>
                <p>Directly Attached: Only exit interface specified.</p>
                <p>Recursive / Fully Specified: Includes next-hop.</p>
                <p>Example:</p>
                <pre>R1(config)# ipv6 route 2001:db8:0:3::/64 g0/0</pre>
                
                <h3>IPv6 Address Types</h3>
                
                <table>
                    <tr><td>Type</td><td>Range</td><td>Description</td></tr>
                    <tr><td>Global Unicast</td><td>2000::/3</td><td>Public, globally unique</td></tr>
                    <tr><td>Unique Local</td><td>FC00::/7 (FD00::/8 preferred)</td><td>Private, non-internet routable</td></tr>
                    <tr><td>Link Local</td><td>FE80::/10</td><td>Auto-generated, local link only</td></tr>
                    <tr><td>Multicast</td><td>FF00::/8</td><td>One-to-many communication</td></tr>
                    <tr><td>Anycast</td><td>Configured manually</td><td>One-to-one-of-many routing</td></tr>
                </table>

                <h3>Multicast Scopes</h3>
                <table>
                    <tr><td>Scope</td><td>Prefix</td><td>Description</td></tr>
                    <tr><td>Interface-local</td><td>FF01</td><td>Stays within device</td></tr>
                    <tr><td>Link-local</td><td>FF02</td><td>Within subnet</td></tr>
                    <tr><td>Site-local</td><td>FF05</td><td>Within physical site</td></tr>
                    <tr><td>Org-local</td><td>FF08</td><td>Within organization</td></tr>
                    <tr><td>Global</td><td>FF0E</td><td>Routable over internet</td></tr>
                </table>

                <h3>IPv6 Header (40 bytes)</h3>
                
                <table>
                    <tr><td>Field</td><td>Length</td><td>Purpose</td></tr>
                    <tr><td>Version</td><td>4 bits</td><td>Indicates IPv6 (value 6)</td></tr>
                    <tr><td>Traffic Class</td><td>8 bits</td><td>QoS priority</td></tr>
                    <tr><td>Flow Label</td><td>20 bits</td><td>Identifies traffic flows</td></tr>
                    <tr><td>Payload Length</td><td>16 bits</td><td>Size of layer 4 segment</td></tr>
                    <tr><td>Next Header</td><td>8 bits</td><td>Indicates TCP/UDP</td></tr>
                    <tr><td>Hop Limit</td><td>8 bits</td><td>Decremented per hop (like TTL)</td></tr>
                    <tr><td>Source/Destination</td><td>128 bits each</td><td>IPv6 addresses</td></tr>
                </table>

                <h2>Network Discovery Protocol (NDP)</h2>

                <h3>Neighbor Discovery</h3>

                <p>Replaces ARP in IPv6 using ICMPv6:</p>
                <p>Neighbor Solicitation (NS) → Type 135 (multicast)</p>
                <p>Neighbor Advertisement (NA) → Type 136</p>

                <h3>Router Discovery</h3>

                <p>Router Solicitation (RS) → Type 133, sent to FF02::2</p>
                <p>Router Advertisement (RA) → Type 134, sent to FF02::1</p>
                <p>Routers announce prefixes and configuration info.</p>

                <h3>SLAAC (Stateless Address Auto-Configuration)</h3>

                <p>Hosts use RS/RA messages to learn prefixes (e.g., 2001:db8::/64) and auto-generate addresses using EUI-64.</p>
                <p>Duplicate Address Detection (DAD)</p>
                <p>Ensures no duplicate IPv6 addresses exist on a link using NS/NA messages.</p>
                
                
                <h2>Subnetting</h2>
                
                <p>Classless-Inter Domain Routing (CIDR)</p>
                <p>Enables flexible subnetting beyond class boundaries. </p>
                
                <p>Internet Authorised Numbers Authority (IANA)</p>
                
                <p>Internet Engineering Task Force (IETF)</p>
                
                <p>Variable Length Subnet Mask (VLSM)</p>
                <p>Allows variable subnet sizes within a network. </p>
                
                
                
            </article>

        </>
    )
};
